import React, { useEffect, useRef, useState } from 'react';
import { healthsherpa } from './api';
import { LogoTile } from './HealthSherpaPresent';
import { fs } from './hsScale';   // 5.31.0: font sizes scale with the page's Text size

// =============================================================================
// Settings -> Carrier logos (5.16.0, admin). Carriers = the state's issuers for the default plan year
// (cached 7 days; one real HealthSherpa call only when that cache is empty) + carriers seen in quotes,
// grouped by name (one carrier can have several HIOS ids — a logo is saved under all of them).
// Bulk drop: each file is matched to a carrier by its file name; fix any match, then "Save all".
// PNG / JPEG / WebP only, at most 2 MB (the server re-encodes to PNG and enforces the same rules).
// =============================================================================

const detailOf = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return e?.message || 'Something went wrong';
};
const OK_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;
const checkFile = (f) => (!OK_TYPES.includes(f.type) ? 'PNG, JPEG or WebP only' : f.size > MAX_BYTES ? 'larger than 2 MB' : null);

export default function CarrierLogos({ c, btn }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState([]);          // [{file, group (index or ''), problem, status}]
  const [drag, setDrag] = useState(false);
  const pickRef = useRef(null);

  const load = () => {
    setError(null);
    healthsherpa.carriers().then((r) => setData(r.data)).catch((e) => setError(detailOf(e)));
  };
  useEffect(load, []);

  const groups = data ? data.carriers : [];
  const firstLogo = (g) => Object.values(g.logos || {})[0] || null;

  const uploadOne = async (file, g) => {
    await healthsherpa.uploadLogo(file, g.issuer_ids, g.name);
  };
  const replace = async (g, file) => {
    const problem = checkFile(file);
    if (problem) { setError(`${file.name}: ${problem}`); return; }
    setBusy(true); setError(null);
    try { await uploadOne(file, g); load(); } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  const remove = async (g) => {
    setBusy(true); setError(null);
    try {
      for (const id of Object.keys(g.logos || {})) await healthsherpa.removeLogo(id);
      load();
    } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !groups.length) return;
    setError(null);
    let proposals = [];
    try {
      const r = await healthsherpa.matchLogos(files.map((f) => f.name), groups.map((g) => ({ name: g.name, issuer_ids: g.issuer_ids })));
      proposals = r.data.matches || [];
    } catch (e) { setError(detailOf(e)); }
    setBulk(files.map((file, i) => {
      const m = proposals[i];
      const gi = m && m.name ? groups.findIndex((g) => g.name === m.name) : -1;
      return { file, group: gi >= 0 ? String(gi) : '', problem: checkFile(file), status: null };
    }));
  };

  const saveAll = async () => {
    setBusy(true); setError(null);
    const next = [...bulk];
    for (let i = 0; i < next.length; i += 1) {
      const row = next[i];
      if (row.problem || row.group === '' || row.status === 'saved') continue;
      try { await uploadOne(row.file, groups[Number(row.group)]); next[i] = { ...row, status: 'saved' }; } catch (e) { next[i] = { ...row, status: detailOf(e) }; }
      setBulk([...next]);
    }
    setBusy(false);
    load();
  };

  return (
    <div>
      {data && <div style={{ fontSize: fs(12), color: c.textSecondary, marginBottom: 8 }}>{data.state} carriers for plan year {data.plan_year}, plus carriers seen in quotes.</div>}
      {error && <div role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: fs(13), margin: '8px 0' }}>{error}</div>}

      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        style={{ border: `2px dashed ${drag ? '#185FA5' : c.borderColor}`, borderRadius: 8, padding: 16, textAlign: 'center', fontSize: fs(13), marginBottom: 12 }}>
        Drop several logo files here (PNG, JPEG or WebP, ≤ 2 MB) — each is matched to a carrier by its file name.{' '}
        <button style={btn(c)} onClick={() => pickRef.current && pickRef.current.click()}>Choose files…</button>
        <input ref={pickRef} type="file" accept="image/png,image/jpeg,image/webp" multiple style={{ display: 'none' }}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {bulk.length > 0 && (
        <div style={{ border: `1px solid ${c.borderColor}`, borderRadius: 8, padding: 10, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Proposed matches — fix any, then Save all</div>
          {bulk.map((row, i) => (
            <div key={`${row.file.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontSize: fs(13) }}>
              <span style={{ width: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.file.name}>{row.file.name}</span>
              <span>→</span>
              <select value={row.group} disabled={!!row.problem || row.status === 'saved'}
                onChange={(e) => setBulk((b) => b.map((x, j) => (j === i ? { ...x, group: e.target.value, status: null } : x)))}
                style={{ padding: '4px 6px', background: c.inputBg, color: c.textPrimary, border: `1px solid ${c.borderColor}`, borderRadius: 4 }}>
                <option value="">— not matched (skip) —</option>
                {groups.map((g, gi) => <option key={g.name} value={String(gi)}>{g.name}</option>)}
              </select>
              <span style={{ color: row.problem ? '#e74c3c' : row.status === 'saved' ? '#2ecc71' : row.status ? '#e74c3c' : c.textSecondary }}>
                {row.problem || (row.status === 'saved' ? '✓ saved' : row.status || '')}
              </span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button style={btn(c)} onClick={() => setBulk([])}>Clear</button>
            <button style={btn(c, true, busy)} disabled={busy} onClick={saveAll}>{busy ? 'Saving…' : 'Save all'}</button>
          </div>
        </div>
      )}

      {!data && !error && <div style={{ fontSize: fs(12), color: c.textSecondary }}>Loading carriers…</div>}
      {groups.map((g) => (
        <div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: `1px solid ${c.borderColor}` }}>
          <div style={{ width: 150, display: 'flex', justifyContent: 'center' }}>
            {firstLogo(g) ? <LogoTile url={firstLogo(g)} name={g.name} height={36} maxWidth={140} darkMode={c.darkMode} /> : <span style={{ fontSize: fs(11), color: c.textSecondary }}>no logo</span>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: fs(13) }}>{g.name}</div>
            <div style={{ fontSize: fs(11), color: c.textSecondary }}>HIOS {g.issuer_ids.join(', ')}</div>
          </div>
          <label style={{ ...btn(c, false, busy), display: 'inline-block' }}>
            {firstLogo(g) ? 'Replace' : 'Upload'}
            <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={busy}
              onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) replace(g, f); }} />
          </label>
          {firstLogo(g) && <button style={btn(c, false, busy)} disabled={busy} onClick={() => remove(g)}>Remove</button>}
        </div>
      ))}
    </div>
  );
}
